import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { AxiosRequestConfig } from 'axios';

import {
  createJobOffering,
  deleteJobOffering,
  extendJobOffering,
  getBillableJobOfferings,
  getJobOffering,
  getJobOfferings,
  getMyJobOfferings,
  updateJobOffering
} from 'api/jobmarketService';
import { CreateJobOffering, JobOffering } from 'types/AppTypes';

/**
 * A custom hook for all jobOfferings from the Jobmarket Service.
 *
 * @returns UseQueryResult<JobOffering[], Error>
 */
export const useJobOfferingsQuery = (config?: AxiosRequestConfig, enabled?: boolean) => {
  const query = useQuery({
    queryKey: ['jobOfferings', config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      const jobOfferings = getJobOfferings(config);
      return jobOfferings;
    },
    placeholderData: keepPreviousData,
    enabled
  });

  return query;
};

export const useMyJobOfferingsQuery = (config?: AxiosRequestConfig) => {
  const query = useQuery({
    queryKey: ['jobOfferings', 'my', config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      const jobOfferings = getMyJobOfferings(config);
      return jobOfferings;
    },
    placeholderData: keepPreviousData
  });

  return query;
};

export const useBillableJobOfferingsQuery = (config?: AxiosRequestConfig) => {
  const query = useQuery({
    queryKey: ['jobOfferings', 'billable', config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      const jobOfferings = getBillableJobOfferings(config);
      return jobOfferings;
    },
    placeholderData: keepPreviousData
  });

  return query;
};

export const useJobOfferingQuery = (id?: number, config?: AxiosRequestConfig) => {
  const query = useQuery({
    queryKey: ['jobOfferings', id, config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      if (!id) {
        return;
      }
      const jobOffering = getJobOffering(id, config);
      return jobOffering;
    },
    enabled: !!id
  });

  return query;
};

export const useJobOfferingsMutation = (config?: AxiosRequestConfig) => {
  const queryClient = useQueryClient();

  const queryKey = ['jobOfferings'];
  const create = useMutation<JobOffering, Error, CreateJobOffering>({
    mutationFn: async args => {
      return await createJobOffering(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  const update = useMutation<JobOffering, Error, { id: number; data: CreateJobOffering }>({
    mutationFn: async args => {
      return await updateJobOffering(args.id, args.data, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  const extend = useMutation<
    JobOffering,
    Error,
    { id: number; data: { jobPriceId: number; date: Date | undefined } }
  >({
    mutationFn: async args => {
      return await extendJobOffering(args.id, args.data, config);
    },
    onSuccess: (data, vars) => {
      queryClient.invalidateQueries({ queryKey: [...queryKey, vars.id] });
    },
    onError: data => {}
  });

  const deletion = useMutation<JobOffering, Error, number>({
    mutationFn: async args => {
      return await deleteJobOffering(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  return { update, create, extend, deletion } as const;
};
